import { useState } from 'react'
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material'
import { PrivateUserInfoType } from '@otw/models'
import { useUsers } from 'hooks/useUsers'
import { CreateDisabledUser } from 'views/SuperAdmin/Management/UserManagement/components/CreateDisabledUser'

interface UserSelectionTabProps {
  selectedUser: PrivateUserInfoType | null
  onUserSelect: (user: PrivateUserInfoType) => void
  onNext: () => void
}

export const UserSelectionTab: React.FC<UserSelectionTabProps> = ({
  selectedUser,
  onUserSelect,
  onNext
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  
  // Use existing useUsers hook
  const { users, isLoading, isError, error } = useUsers()

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleUserClick = (user: PrivateUserInfoType) => {
    onUserSelect(user)
    // Auto-proceed to next step when user is selected
    setTimeout(() => onNext(), 100)
  }

  if (isError) {
    return (
      <Alert severity="error">
        Error loading users: {error?.message}
      </Alert>
    )
  }

  return (
    <Box sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom>
        Select User for Photo Upload
      </Typography>
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <CreateDisabledUser
          onSuccess={(newUser) => {
            onUserSelect(newUser)
            setTimeout(() => onNext(), 100)
          }}
        />
      </Box>
      
      <TextField
        fullWidth
        label="Search users by email or name"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <List sx={{ flex: 1, overflow: 'auto' }}>
          {filteredUsers.map((user) => (
            <ListItem key={user.id} disablePadding>
              <ListItemButton
                selected={selectedUser?.id === user.id}
                onClick={() => handleUserClick(user)}
              >
                <ListItemAvatar>
                  <Avatar>
                    {user.first_name?.[0]}{user.last_name?.[0]}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${user.first_name} ${user.last_name}`}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Role: {user.role} {user.phone && `• Phone: ${user.phone}`}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
          
          {filteredUsers.length === 0 && searchTerm && (
            <ListItem>
              <ListItemText primary="No users found" />
            </ListItem>
          )}
        </List>
      )}
    </Box>
  )
} 